import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Input, Field, Select } from '@itds/elements/index.js';

import { AgencyContext } from '@brokerage/contexts/AgencyContext';

const getRateLimitValues = aq => ({
  rate_limit_replenishment_period_seconds:
    'rate_limit_replenishment_period_seconds' in aq ? aq.rate_limit_replenishment_period_seconds : '',
  rate_limit_token_limit: 'rate_limit_token_limit' in aq ? aq.rate_limit_token_limit : '',
  rate_limit_tokens_per_period: 'rate_limit_tokens_per_period' in aq ? aq.rate_limit_tokens_per_period : ''
});

const getMonthlyUsageValues = aq => ({
  monthly_request_limit: 'monthly_request_limit' in aq ? aq.monthly_request_limit : ''
});

const premiumThresholdOptions = [
  { value: 0.01, label: 'Exact Match' },
  ...Array(40)
    .fill()
    .map((_, i) => {
      const value = 10 - i * 0.25;

      return {
        value,
        label: `${value.toFixed(2)}%`
      };
    })
];

const General = ({ isUQDeactivated = false, bga_quoter = {}, handleSave, token }) => {
  const aq = bga_quoter || {};
  const { member_names } = useContext(AgencyContext);
  const [data, setData] = useState({
    is_deactivated: Boolean(isUQDeactivated || ('is_deactivated' in aq ? aq.is_deactivated : false)),
    name: 'name' in aq ? aq.name : '',
    member_id: 'member_id' in aq ? aq.member_id : '',
    is_live: 'is_live' in aq ? aq.is_live : false,
    is_beta: 'is_beta' in aq ? aq.is_beta : false,
    vive_score_enabled: 'vive_score_enabled' in aq ? aq.vive_score_enabled : false,
    default_sort_hexure_score: 'default_sort_hexure_score' in aq ? aq.default_sort_hexure_score : false,
    product_limits: 'product_limits' in aq ? aq.product_limits : 3,
    enable_quote_by_premium: 'enable_quote_by_premium' in aq ? aq.enable_quote_by_premium : false,
    quote_premium_threshold: 'quote_premium_threshold' in aq ? aq.quote_premium_threshold : 5.00, // Default to 5% if not provided
    default_face_amount: 'default_face_amount' in aq ? aq.default_face_amount : '',
    minimum_face_amount: 'minimum_face_amount' in aq ? aq.minimum_face_amount : '',
    maximum_face_amount: 'maximum_face_amount' in aq ? aq.maximum_face_amount : '',
    required_agent_info: Array.isArray(aq['required_agent_info']) ? aq['required_agent_info'] : [],
    hide_onboarding: 'hide_onboarding' in aq ? aq.hide_onboarding : false,
    is_health_analyzer_enabled: 'is_health_analyzer_enabled' in aq ? aq.is_health_analyzer_enabled : false,
    allow_options_icon: 'allow_options_icon' in aq ? aq.allow_options_icon : false,
    allow_forms: 'allow_forms' in aq ? aq.allow_forms : false,
    health_analyzer_token: 'health_analyzer_token' in aq ? aq.health_analyzer_token : '',
    rate_limit_enabled: 'rate_limit_enabled' in aq ? aq.rate_limit_enabled : false,
    ...getRateLimitValues(aq),
    show_carrier_limiting_ui:
      'show_carrier_limiting_ui' in aq ? aq.show_carrier_limiting_ui : false,
    show_download_pdf: 'show_download_pdf' in aq ? aq.show_download_pdf : false,
    show_text_pdf: 'show_text_pdf' in aq ? aq.show_text_pdf : false,
    show_email_pdf: 'show_email_pdf' in aq ? aq.show_email_pdf : false,
    monthly_limit_enabled: 'monthly_limit_enabled' in aq ? aq.monthly_limit_enabled : false,
    ...getMonthlyUsageValues(aq),
    notes: 'notes' in aq ? aq.notes : '',
    is_archived: Boolean(isUQDeactivated || ('is_archived' in aq ? aq.is_archived : false)),
    token: 'token' in aq ? aq.token : ''
  });
  const [errors, setErrors] = useState([]);

  const handleArchiveToggle = () => {
    setData({ ...data, is_archived: !data.is_archived });
    handleSave({ is_archived: !data.is_archived });
  };

  const handleChange = (name, e) => {
    setData(prevData => ({ ...prevData, [name]: e.target.value }));
  };

  const handleCheck = (name, e) => {
    const checked = e.target.checked;

    setData(prevData => {
      if (name === 'rate_limit_enabled' && !checked) {
        return {
          ...prevData,
          rate_limit_enabled: checked,
          ...getRateLimitValues(aq)
        };
      }

      if (name === 'monthly_limit_enabled' && !checked) {
        return {
          ...prevData,
          monthly_limit_enabled: checked,
          ...getMonthlyUsageValues(aq)
        };
      }

      return { ...prevData, [name]: checked };
    });
  };

  const handleAgentInfoToggle = (name, e) => {
    const required_agent_info = data.required_agent_info.filter(n => n !== name);

    if (e.target.checked) {
      required_agent_info.push(name);
    }

    setData({ ...data, required_agent_info });
  };

  const rateLimitsReadOnly = !data.rate_limit_enabled || data?.is_deactivated || data?.is_archived;
  const monthlyUsageReadOnly =
    !data.monthly_limit_enabled || data?.is_deactivated || data?.is_archived;
  const sectionHeaderStyle = {
    color: '#666',
    fontSize: 13,
    fontWeight: 600,
    margin: '0 0 12px'
  };

  const handleDataSourceToggle = () => {
    setData({
      ...data,
      is_live: !data.is_live,
      is_beta: !data.is_beta
    });
  };

  const save = () => {
    const errors = [];

    if (!data.name) {
      errors.push('name');
    }

    setErrors(errors);

    if (!errors.length) {
      if (data.maximum_face_amount < data.default_face_amount) {
        const updated_data = data;

        updated_data.default_face_amount = data.maximum_face_amount;
        handleSave(updated_data);
      } else handleSave(data);
    }
  };

  return (
    <>
      <Field label='Token'>
        <Input readOnly={true} value={token ? token : aq.token || ''} />
      </Field>

      <Field label=''>
        <Checkbox
          checked={data.is_deactivated}
          disabled={data.is_archived}
          label='Deactivated'
          onChange={handleCheck.bind(null, 'is_deactivated')}
        />
      </Field>

      <Field label='Name'>
        <Input
          invalid={errors.includes('name')}
          onChange={handleChange.bind(null, 'name')}
          readOnly={data?.is_deactivated || data?.is_archived}
          required={true}
          value={data.name || ''}
        />
      </Field>

      <Field label='Member'>
        <Select
          onChange={handleChange.bind(null, 'member_id')}
          options={member_names}
          placeholder='-- Select Member --'
          readOnly={data?.is_deactivated || data?.is_archived}
          value={data.member_id || ''}
        />
      </Field>
      <Field label='Data Source'>
        <Select
          onChange={handleDataSourceToggle}
          options={[
            { value: 'is_live', label: 'Live' },
            { value: 'is_beta', label: 'Beta' }
          ]}
          readOnly={data?.is_deactivated || data?.is_archived}
          value={data.is_live ? 'is_live' : 'is_beta'}
        />
      </Field>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ width: '49%' }}>
          <Field label='Default Face Amount'>
            <Input
              onChange={handleChange.bind(null, 'default_face_amount')}
              readOnly={data?.is_deactivated || data?.is_archived}
              value={data.default_face_amount || ''}
            />
          </Field>
        </div>
        <div style={{ width: '49%' }}>
          <Field label='Product Limit'>
            <Select
              onChange={handleChange.bind(null, 'product_limits')}
              options={[
                { value: 3, label: '3' },
                { value: 4, label: '4' },
                { value: 5, label: '5' },
                { value: 6, label: '6' },
                { value: 7, label: '7' },
                { value: 8, label: '8' }
              ]}
              readOnly={data?.is_deactivated || data?.is_archived}
              value={data.product_limits || 3}
            />
          </Field>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ width: '49%' }}>
          <Field label='Minimum Face Amount'>
            <Input
              onChange={handleChange.bind(null, 'minimum_face_amount')}
              readOnly={data?.is_deactivated || data?.is_archived}
              value={data.minimum_face_amount || ''}
            />
          </Field>
        </div>
        <div style={{ width: '49%' }}>
          <Field label='Maximum Face Amount'>
            <Input
              invalid={errors.includes('maximum_face_amount')}
              onChange={handleChange.bind(null, 'maximum_face_amount')}
              readOnly={data?.is_deactivated || data?.is_archived}
              value={data.maximum_face_amount || ''}
            />
          </Field>
        </div>
      </div>

      <Field
        description="Select the Agent Information that is required to run a quote If you don't want any information to be required, then don't check any of the boxes."
        label='Required Agent Information'
      >
        <Checkbox
          checked={data.required_agent_info ? data.required_agent_info.includes('email') : false}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Email'
          onChange={handleAgentInfoToggle.bind(null, 'email')}
        />

        <Checkbox
          checked={data.required_agent_info ? data.required_agent_info.includes('phone') : false}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Phone'
          onChange={handleAgentInfoToggle.bind(null, 'phone')}
        />

        <Checkbox
          checked={
            data.required_agent_info ? data.required_agent_info.includes('first_name') : false
          }
          disabled={data?.is_deactivated || data?.is_archived}
          label='First Name'
          onChange={handleAgentInfoToggle.bind(null, 'first_name')}
        />

        <Checkbox
          checked={
            data.required_agent_info ? data.required_agent_info.includes('middle_name') : false
          }
          disabled={data?.is_deactivated || data?.is_archived}
          label='Middle Name'
          onChange={handleAgentInfoToggle.bind(null, 'middle_name')}
        />

        <Checkbox
          checked={
            data.required_agent_info ? data.required_agent_info.includes('last_name') : false
          }
          disabled={data?.is_deactivated || data?.is_archived}
          label='Last Name'
          onChange={handleAgentInfoToggle.bind(null, 'last_name')}
        />

        <Checkbox
          checked={
            Array.isArray(data.required_agent_info) &&
            data.required_agent_info.includes('insurance_license_number')
          }
          disabled={data?.is_deactivated || data?.is_archived}
          label='Insurance License Number'
          onChange={handleAgentInfoToggle.bind(null, 'insurance_license_number')}
        />
      </Field>
      <Field label='Quote Type'>
        <Checkbox
          checked={data.enable_quote_by_premium}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Quote by Premium'
          onChange={handleCheck.bind(null, 'enable_quote_by_premium')}
        />
        {data.enable_quote_by_premium && (
          <div style={{ marginTop: '10px', width: '49%' }}>
            <Field label='Premium Threshold (%)'>
              <Select
                onChange={handleChange.bind(null, 'quote_premium_threshold')}
                options={premiumThresholdOptions}
                readOnly={data?.is_deactivated || data?.is_archived}
                value={data.quote_premium_threshold}
              />
            </Field>
          </div>
        )}
      </Field>

      <Field label='Features'>
        <Checkbox
          checked={data.allow_forms}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Display quote forms'
          onChange={handleCheck.bind(null, 'allow_forms')}
        />
      </Field>

      <Field label='Features'>
        <Checkbox
          checked={data.allow_forms}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Display quote forms'
          onChange={handleCheck.bind(null, 'allow_forms')}
        />
        <Checkbox
          checked={data.is_health_analyzer_enabled}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Hexure Health Analyzer'
          onChange={handleCheck.bind(null, 'is_health_analyzer_enabled')}
        />
        {/* Health Analyzer API Token input */}
        <Field label='Health Analyzer ApiToken' style={{ marginTop: 10 }}>
          <Input
            onChange={handleChange.bind(null, 'health_analyzer_token')}
            readOnly={data?.is_deactivated || data?.is_archived}
            value={data.health_analyzer_token || ''}
          />
        </Field>
        {/* vive score related options */}
        <Checkbox
          checked={data.vive_score_enabled}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Display Product Score'
          onChange={handleCheck.bind(null, 'vive_score_enabled')}
        />
        <Checkbox
          checked={data.default_sort_hexure_score}
          disabled={!data.vive_score_enabled || data?.is_deactivated || data?.is_archived}
          label='Default Sort by Product Score'
          onChange={handleCheck.bind(null, 'default_sort_hexure_score')}
        />
      </Field>

      <div style={{ marginBottom: 20 }}>
        <div style={sectionHeaderStyle}>Rate Limits</div>
        <Field label=''>
          <Checkbox
            checked={data.rate_limit_enabled}
            disabled={data?.is_deactivated || data?.is_archived}
            label='Enabled'
            onChange={handleCheck.bind(null, 'rate_limit_enabled')}
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ width: '49%' }}>
            <Field label='Token Limit'>
              <Input
                onChange={handleChange.bind(null, 'rate_limit_token_limit')}
                readOnly={rateLimitsReadOnly}
                type='number'
                value={data.rate_limit_token_limit || ''}
              />
            </Field>
          </div>
          <div style={{ width: '49%' }}>
            <Field label='Tokens Per Period'>
              <Input
                onChange={handleChange.bind(null, 'rate_limit_tokens_per_period')}
                readOnly={rateLimitsReadOnly}
                type='number'
                value={data.rate_limit_tokens_per_period || ''}
              />
            </Field>
          </div>
        </div>

        <div style={{ width: '49%' }}>
          <Field label='Replenishment Period Seconds'>
            <Input
              onChange={handleChange.bind(null, 'rate_limit_replenishment_period_seconds')}
              readOnly={rateLimitsReadOnly}
              type='number'
              value={data.rate_limit_replenishment_period_seconds || ''}
            />
          </Field>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={sectionHeaderStyle}>Monthly Usage</div>
        <Field label=''>
          <Checkbox
            checked={data.monthly_limit_enabled}
            disabled={data?.is_deactivated || data?.is_archived}
            label='Enabled'
            onChange={handleCheck.bind(null, 'monthly_limit_enabled')}
          />
        </Field>

        <div style={{ width: '49%' }}>
          <Field label='Monthly Request Limit'>
            <Input
              onChange={handleChange.bind(null, 'monthly_request_limit')}
              readOnly={monthlyUsageReadOnly}
              type='number'
              value={data.monthly_request_limit || ''}
            />
          </Field>
        </div>
      </div>

      <Field label='Notes'>
        <Input
          height={100}
          onChange={handleChange.bind(null, 'notes')}
          readOnly={data?.is_deactivated || data?.is_archived}
          type='textarea'
          value={data.notes || ''}
        />
      </Field>

      <div style={{ display: 'flex', marginTop: 40 }}>
        <Button color='RED' onClick={handleArchiveToggle} style={{ marginRight: '5px' }}>
          {data.is_archived ? 'Unarchive' : 'Archive'}
        </Button>
        {data.is_archived ? null : (
          <Button onClick={save} style={{}}>
            Save Changes
          </Button>
        )}
      </div>
    </>
  );
};

General.propTypes = {
  bga_quoter: PropTypes.object,
  handleSave: PropTypes.func,
  isUQDeactivated: PropTypes.bool,
  token: PropTypes.string
};

export default General;
